import { getOrgCrmListByOrgId } from '@/features/crm/hooks/useQuery/useGetOrganisationCrmList';
import { getCrmConfigsByProjectId } from '@/features/crm_config/hooks/useQuery/useCrmConfigsByCrmProjectId';
import { getAllCrmProjectsList } from '@/features/crm_project/hooks/useQuery/useAllCrmProjectsList';
import { getCrmProjectById } from '@/features/crm_project/hooks/useQuery/useGetCrmProjectById';
import { getCrmProjectsByCrmId } from '@/features/crm_project/hooks/useQuery/useGetCrmProjectsByCrmId';
import { CrmSystemIds } from '@/features/crm_system/lib/_core/crmSystem/crmSystem.types.gen';
import { getOrganizationCrmConnectionsItems } from '@/features/organization/hooks/useQuery/useOrganizationCrmConnectionsItems/useOrganizationCrmConnectionsItems.query';
import { getQuestionsBySurveyId } from '@/features/question/hooks/useQuery/useGetQuestionsBySurveyId';
import { getOrganisationQuestions } from '@/features/question_bank/hooks/useQuery/useGetOrganisationQuestions';
import { getQuestionBankQuestions } from '@/features/question_bank/hooks/useQuery/useGetQuestionBankQuestions';
import { getSurveyById } from '@/features/survey/hooks/useQuery/useGetSurveyById';
import { getAllSurveyBankTemplates } from '@/features/survey_bank/hooks/useQuery/useGetAllSurveyBankTemplates';
import type { GetSingleSurveyFromBankProps } from '@/features/survey_bank/hooks/useQuery/useGetSingleSurveyFromBank';
import { getSingleSurveyFromBank } from '@/features/survey_bank/hooks/useQuery/useGetSingleSurveyFromBank';
import type { GetSurveyBankProps } from '@/features/survey_bank/hooks/useQuery/useGetSurveyBank';
import { getSurveyBank } from '@/features/survey_bank/hooks/useQuery/useGetSurveyBank';
import { getUserData } from '@/features/user/hooks/useQuery/useUserData';
import {
  createQueryKeys,
  mergeQueryKeys,
} from '@lukemorales/query-key-factory';
import { getOrganizationById } from '../../features/organization/hooks/useQuery/useGetOrganizationById';
import { getOrganizations } from '../../features/organization/hooks/useQuery/useGetOrganizations';
import { getCrmProjectConnections } from '../../features/project/hooks/useQuery/useCrmProjectConnections';
import type { GetSurveysByProjectIdProps } from '../../features/survey/hooks/useQuery/useGetSurveysByProjectId';
import { getSurveysByProjectId } from '../../features/survey/hooks/useQuery/useGetSurveysByProjectId';
import { Tables } from '../supabase/supabase_types_override';
import { getAirtableBases } from '../utils/crm/airtable/base';
import { getTable, getTables } from '../utils/crm/airtable/tables';
import {
  getProjectById,
  GetProjectByIdProps,
} from './project/useGetProjectById';
import type { GetProjectsByOrgIdAndCrmSystemProps } from './project/useGetProjectsByOrgIdAndCrmSystem';
import { getProjectsByOrgIdAndCrmSystem } from './project/useGetProjectsByOrgIdAndCrmSystem';
import type { GetProjectsListProps } from './project/useProjectsList';
import { getProjectsList } from './project/useProjectsList/useProjectsList.query';
import type { GetQuestionBankQuestionsProps } from './question/useGetQuestionBankQuestions';
import type { getQuestionsBySurveyIdProps } from './question/useGetQuestionsBySurveyId';
import type { useGetExportDataProps } from './useGetExportData';
import { getExportData } from './useGetExportData';
import { getCrmById } from '@/features/crm/hooks/useQuery/useGetCrmById';

const airtableQueryKeys = createQueryKeys('airtable', {
  base: (personalAccessToken?: string) => ({
    queryKey: [personalAccessToken],
    contextQueries: {
      list: {
        queryKey: null,
        queryFn: () => getAirtableBases(personalAccessToken),
      },
    },
  }),
  table: (baseId: string, apiKey: string) => ({
    queryKey: [baseId, apiKey],
    contextQueries: {
      details: (tableNameOrId: string) => ({
        queryKey: [tableNameOrId],
        queryFn: () =>
          getTable({
            baseId: baseId,
            apiKey: apiKey,
            tableNameOrId: tableNameOrId,
          }),
      }),
      list: {
        queryKey: null,
        queryFn: () =>
          getTables({
            baseId: baseId,
            apiKey: apiKey,
          }),
      },
    },
  }),
});

const crmQueryKeys = createQueryKeys('crm', {
  details: (crmId: Tables<'crm'>['id']) => ({
    queryKey: [crmId],
    queryFn: () => getCrmById(crmId),
  }),
  list: {
    queryKey: null,
    contextQueries: {
      // useGetOrganisationCrmList
      byOrgId: (orgId: Tables<'organisation'>['id']) => ({
        queryKey: [orgId, 'organisation_crm', 'crm_config'],
        queryFn: () => getOrgCrmListByOrgId(orgId),
      }),
    },
  },
});

const crmProjectQueryKeys = createQueryKeys('crm_project', {
  byId: (id: Tables<'crm_project'>['id']) => ({
    queryKey: [id],
    queryFn: () => getCrmProjectById({ crmProjectId: id }),
  }),
  list: {
    queryKey: null,
    contextQueries: {
      byOrgId: (orgId: Tables<'organisation'>['id']) => ({
        queryKey: [orgId],
        contextQueries: {
          // useAllCrmProjectsList
          byCrmSystemsArr: (whereCrmSystems?: CrmSystemIds[]) => ({
            queryKey: [whereCrmSystems, 'crm', 'organisation_crm'],
            queryFn: () => getAllCrmProjectsList({ whereCrmSystems, orgId }),
          }),
        },
      }),
      // useGetCrmProjectsByCrmId
      byCrmId: (crmId: Tables<'crm'>['id']) => ({
        queryKey: [crmId],
        queryFn: () => getCrmProjectsByCrmId(crmId!),
      }),
    },
  },
});

const crmConfigQueryKeys = createQueryKeys('crm_config', {
  list: {
    queryKey: null,
    contextQueries: {
      // useCrmConfigsByCrmProjectId
      byCrmProjectId: (crmProjectId: Tables<'crm_project'>['id']) => ({
        queryKey: [crmProjectId],
        queryFn: () => getCrmConfigsByProjectId(crmProjectId),
      }),
    },
  },
});

const userQueryKeys = createQueryKeys('user', {
  // useUserData (features/user)
  data: {
    queryKey: null,
    queryFn: getUserData,
  },
});

// Query keys with queryFn - organized by table
const surveyQueryKeys = createQueryKeys('survey', {
  // useGetSurveyById
  details: (surveyId: Tables<'survey'>['id']) => ({
    queryKey: [surveyId],
    queryFn: () => getSurveyById(surveyId),
  }),
  list: {
    queryKey: null,
    contextQueries: {
      // useGetSurveysByProjectId
      byProjectId: (props: GetSurveysByProjectIdProps) => ({
        queryKey: [
          props.projectId,
          props.archived ?? false,
          props.sortByCreatedAtAscending ?? false,
        ],
        queryFn: () => getSurveysByProjectId(props),
      }),
    },
  },
});

const questionQueryKeys = createQueryKeys('question', {
  list: {
    queryKey: null,
    contextQueries: {
      // useGetQuestionsBySurveyId
      bySurveyId: (props: getQuestionsBySurveyIdProps) => ({
        queryKey: [
          props.surveyId,
          'user',
          'response',
          'survey',
          'project',
          'project_members',
          props.isArchived,
          props.sortByCreatedAtAscending,
        ],
        queryFn: () => getQuestionsBySurveyId(props),
      }),
    },
  },
});

const questionBankQueryKeys = createQueryKeys('question_bank', {
  list: {
    queryKey: null,
    contextQueries: {
      byOrgId: (orgId: Tables<'organisation'>['id']) => ({
        queryKey: [orgId],
        contextQueries: {
          // useGetQuestionBankQuestions
          byCrmSystemsArr: (
            props: Omit<GetQuestionBankQuestionsProps, 'organisationId'>
          ) => ({
            queryKey: [props.crmSystem, props.search],
            queryFn: () =>
              getQuestionBankQuestions({ ...props, organisationId: orgId }),
          }),
          // useGetOrganisationQuestions
          byCrmId: (crmId: Tables<'crm'>['id']) => ({
            queryKey: [crmId],
            queryFn: () =>
              getOrganisationQuestions({
                organisation_id: orgId,
                crm_id: crmId,
              }),
          }),
        },
      }),
    },
  },
});

const surveyBankQueryKeys = createQueryKeys('survey_bank', {
  // useGetSingleSurveyFromBank
  details: (props: GetSingleSurveyFromBankProps) => ({
    queryKey: [props.surveyId, 'user', 'survey_bank_question_bank'],
    queryFn: () => getSingleSurveyFromBank(props),
  }),
  list: (orgId: Tables<'organisation'>['id']) => ({
    queryKey: [orgId],
    contextQueries: {
      // useGetAllSurveyBankTemplates, useGetSurveyBank
      byCrmSystemsArr: (crmSystems?: CrmSystemIds[]) => ({
        queryKey: [crmSystems, 'user', 'author', 'survey_bank_question_bank'],
        queryFn: () =>
          getAllSurveyBankTemplates({ whereCrmSystems: crmSystems, orgId }),
        contextQueries: {
          // useGetSurveyBank
          bySearch: (search?: GetSurveyBankProps['search']) => ({
            queryKey: [search, 'user', 'author', 'survey_bank_question_bank'],
            queryFn: () =>
              getSurveyBank({
                crmSystem: crmSystems,
                organisationId: orgId,
                search,
              }),
          }),
        },
      }),
    },
  }),
});

const organisationQueryKeys = createQueryKeys('organisation', {
  // useGetOrganizationById
  details: (orgId: Tables<'organisation'>['id']) => ({
    queryKey: [orgId, 'organisation_members', 'user', 'owner'],
    queryFn: () => getOrganizationById(orgId),
    contextQueries: {
      crmConnectionsItems: () => ({
        queryKey: ['crm_connections_items'],
        queryFn: () => getOrganizationCrmConnectionsItems(orgId),
      }),
    },
  }),
  // useGetOrganizations
  list: () => ({
    queryKey: ['organisation_members', 'user', 'owner'],
    queryFn: () => getOrganizations(),
  }),
});

const projectQueryKeys = createQueryKeys('project', {
  // useGetProjectById
  details: (props: GetProjectByIdProps) => ({
    queryKey: [
      props.projectId,
      'project_members',
      'user',
      'crm_project',
      'crm',
      'crm_config',
      'manager',
      'organisation',
    ],
    queryFn: () => getProjectById(props),
  }),
  list: {
    queryKey: null,
    contextQueries: {
      // useProjectsList
      byOrgId: (props: GetProjectsListProps) => ({
        queryKey: [
          props.orgId,
          props.sortByCreatedAtAscending,
          'project_members',
          'user',
          'crm_project',
          'crm',
          'crm_config',
          'manager',
          'organisation',
          'survey',
        ],
        queryFn: () => getProjectsList(props),
        contextQueries: {
          // useGetProjectsByOrgIdAndCrmSystem
          byCrmSystem: (
            filterProps: Omit<GetProjectsByOrgIdAndCrmSystemProps, 'orgId'>
          ) => ({
            queryKey: [
              filterProps.crmSystem,
              filterProps.archived ?? false,
              filterProps.sortByCreatedAtAscending ?? false,
            ],
            queryFn: () =>
              getProjectsByOrgIdAndCrmSystem({
                ...filterProps,
                orgId: props.orgId,
              }),
          }),
        },
      }),
      // useCrmProjectConnections
      byCrmProjectId: (crmProjectId: Tables<'crm_project'>['id']) => ({
        queryKey: [crmProjectId],
        queryFn: () => getCrmProjectConnections({ crmProjectId }),
      }),
    },
  },
  // useGetExportData
  export: (props: useGetExportDataProps) => ({
    queryKey: [
      props.projectId,
      props.surveyId,
      'survey',
      'crm_config',
      'question',
      'response',
      'user',
      'crm',
      'organisation',
      'crm_project',
    ],
    queryFn: () => getExportData(props),
  }),
});

const responseQueryKeys = createQueryKeys('response', {});

const projectMembersQueryKeys = createQueryKeys('project_members', {});
export const queryKeys = mergeQueryKeys(
  airtableQueryKeys,
  crmQueryKeys,
  crmProjectQueryKeys,
  crmConfigQueryKeys,
  userQueryKeys,
  surveyQueryKeys,
  questionQueryKeys,
  questionBankQueryKeys,
  surveyBankQueryKeys,
  organisationQueryKeys,
  projectQueryKeys,
  responseQueryKeys,
  projectMembersQueryKeys
);
